/**
 * VP-302 — jedna reguła renderowania metryk w drill-downzie (kampania → zestaw → reklama).
 *
 * Do tej pory każda komórka pisała `x > 0 ? format(x) : "—"`, więc TRZY różne sytuacje wyglądały
 * identycznie i klient czytał je jako „aplikacja nie działa":
 *   1. realnie zero zdarzeń,
 *   2. zdarzenia są, ale nie wiemy, do którego obiektu Meta należą,
 *   3. backend w ogóle nie przysłał tej metryki.
 *
 * Rozdzielamy je dwoma komponentami:
 *  - `MetricCount` — LICZNIK zdarzeń. `0` jest wynikiem pomiaru, więc renderujemy `0`, nigdy „—".
 *    Myślnik zostaje wyłącznie dla `null` (przypadek 3) i zawsze z tooltipem, żeby nie udawał zera.
 *    Uwaga: przy metricsV2 ON kampania z wydatkiem ZAWSZE ma wiersz (agregat dosiewa grupy po
 *    samym wydatku), więc `null` na tym poziomie oznacza brak atrybucji z CRM — stąd treść tooltipa.
 *  - `MetricRatio` — wartość POCHODNA (koszt/lead, show rate, ROAS). Tu `0` byłoby kłamstwem: bez
 *    mianownika niczego nie policzyliśmy. Zostaje „—", ale z tooltipem mówiącym, czego brakuje.
 *
 * Przypadek 2 („nieprzypisane") NIE jest stanem komórki — to osobny wiersz reszty pod rozwiniętą
 * kampanią, bo dotyczy różnicy między rodzicem a sumą dzieci, a nie pojedynczej liczby.
 */
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

type MetricCountProps = {
  /** Liczba zdarzeń. `null`/`undefined` = nie mamy danych konwersji dla tego wiersza (≠ zero). */
  value: number | null | undefined;
  /** Formatter z widoku (Intl siedzi w `dashboard.tsx`, nie duplikujemy konfiguracji). */
  format: (value: number) => string;
  className?: string;
  /** Tooltip dla wartości — np. wyjaśnienie, skąd wzięła się liczba. */
  title?: string;
};

export function MetricCount({ value, format, className, title }: MetricCountProps) {
  const { t } = useTranslation();
  if (value == null) {
    return (
      <span
        // Klasa wołającego IDZIE PIERWSZA: twMerge zostawia ostatni kolor tekstu, a stan „brak
        // danych" ma być wyszarzony niezależnie od tego, jak koloruje się liczba w tej kolumnie.
        className={cn(className, "text-muted-foreground cursor-help")}
        title={t("dashboard.table.metricMissing")}
      >
        —
      </span>
    );
  }
  return (
    <span className={className} title={title}>
      {format(value)}
    </span>
  );
}

type MetricRatioProps = {
  /** Wyliczona wartość albo `null`, gdy mianownik był zerowy / brakło składnika. */
  value: number | null | undefined;
  format: (value: number) => string;
  /** Czemu nie da się policzyć — trafia do tooltipa przy „—". Wymagane: bez tego wracamy do bugu. */
  unavailableReason: string;
  className?: string;
};

export function MetricRatio({ value, format, unavailableReason, className }: MetricRatioProps) {
  if (value == null || !Number.isFinite(value)) {
    return (
      <span
        className={cn(className, "text-muted-foreground cursor-help")}
        title={unavailableReason}
      >
        —
      </span>
    );
  }
  return <span className={className}>{format(value)}</span>;
}
